import { nanoid } from "nanoid";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { TContainerMode } from "../utils/types/containers/XContainers.types";
import { useSaveData } from "./useSaveData";
import { USE_SELECTED_INDEX } from "./useSelectedElement";
import {
  isCommon,
  useXCommonChildrenUpdater,
  USE_X_COMMON_CHILDREN_SELECTOR,
} from "./useXCommon";
import { TUseXElement, USE_X_ELEMENT_SELECTOR } from "./useXElement";
import { useXPageUpdater } from "./useXPage";

export const useCloneElement = () => {
  const selectedIndex = useRecoilValue(USE_SELECTED_INDEX);
  const setXPage = useXPageUpdater();
  const setCommon = useXCommonChildrenUpdater(selectedIndex as any);
  const saveData = useSaveData();

  return useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        try {
          /**
           * SAVE THE CURRENT STRUCTURE FOR SAFETY
           */
          await saveData();

          /**
           * GET THE SELECTED ELEMENT
           * THAT IS THE ELEMENT TO CLONE
           */

          const index = await snapshot.getPromise(USE_SELECTED_INDEX);

          /**
           * IF WE DO NOT FIND ELEMNT INDEX WE ARE ON THE PAGE
           * IF THE ELEMENT IS COMMON ROOT ELEMENT
           * IT IS NOT CLONABLE WE STOP
           */

          if (!index || isCommon(index, true)) return;

          //!  LET'S DO A SIMPLE CLONE FIRST
          const localId = nanoid(3);

          const template: TContainerMode[] = [];

          const createElement = async (mainId: string) => {
            const element: TUseXElement<any> = await snapshot.getPromise(
              USE_X_ELEMENT_SELECTOR(mainId)
            );

            if (!element || isCommon(element.index, true)) return;

            /**
             * CREATING THE CLONE
             */
            const clone: any = {
              name: element.name,
              config: {
                index: element.index + localId,
              },
              props: element.value ?? { all: {} },
            };

            /**
             * ASSIGNING EXISTING VALUES
             */
            if (element.childOf) {
              clone.config.childOf =
                element.childOf + (index === element.index ? "" : localId);
            }

            if (element.hierachy) clone.config.hierachy = element.hierachy;

            if (element.dataFetching) clone.dataFetching = element.dataFetching;

            template.push(clone);

            await Promise.all(
              element.elementsIndexes.map(async (id) => await createElement(id))
            );
          };

          await createElement(index);

          if (isCommon(index) && setCommon) {
            const old = await snapshot.getPromise(
              USE_X_COMMON_CHILDREN_SELECTOR(index)
            );
            setCommon(() => {
              return [...(old as any), ...template];
            });
          } else {
            setXPage((old) => [...(old as any), ...template]);
          }
        } catch (error) {
          console.error(error);
        }
      },
    []
  );
};
